import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { motion, AnimatePresence } from "framer-motion";
import * as m from "~/paraglide/messages";

/* ── Types ── */

interface CommandItem {
  id: string;
  label: string;
  to: string;
  group: "navigation" | "actions";
  icon?: React.ReactNode;
}

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

/* ── Search icon (inline SVG) ── */

const SearchIcon = (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 16, height: 16, opacity: 0.4 }}
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

/* ── Nav icon builder (matches SidebarIcons style) ── */

function navIcon(children: React.ReactNode) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  );
}

/* ── Command items (hardcoded, mirrors sidebar) ── */

function getCommandItems(): CommandItem[] {
  return [
    /* Navigation — Primary */
    {
      id: "dashboard",
      label: m.nav_dashboard(),
      to: "/dashboard",
      group: "navigation",
      icon: navIcon(
        <>
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </>,
      ),
    },
    {
      id: "jobs",
      label: m.nav_jobs(),
      to: "/jobs",
      group: "navigation",
      icon: navIcon(
        <>
          <rect x="2" y="7" width="20" height="14" rx="2" />
          <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
        </>,
      ),
    },
    {
      id: "resumes",
      label: m.nav_resumes(),
      to: "/resumes",
      group: "navigation",
      icon: navIcon(
        <>
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </>,
      ),
    },
    {
      id: "applications",
      label: m.nav_applications(),
      to: "/applications",
      group: "navigation",
      icon: navIcon(
        <>
          <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </>,
      ),
    },
    {
      id: "cover-letters",
      label: m.nav_cover_letters(),
      to: "/cover-letters",
      group: "navigation",
      icon: navIcon(
        <>
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
          <polyline points="22,6 12,13 2,6" />
        </>,
      ),
    },
    /* Navigation — Secondary (Tools) */
    {
      id: "analytics",
      label: m.nav_analytics(),
      to: "/analytics",
      group: "navigation",
      icon: navIcon(
        <>
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
        </>,
      ),
    },
    {
      id: "interview-prep",
      label: m.nav_interview(),
      to: "/interview-prep",
      group: "navigation",
      icon: navIcon(
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />,
      ),
    },
    {
      id: "salary",
      label: m.nav_salary(),
      to: "/salary",
      group: "navigation",
      icon: navIcon(
        <>
          <line x1="12" y1="1" x2="12" y2="23" />
          <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
        </>,
      ),
    },
    {
      id: "deep-match",
      label: m.nav_deep_match(),
      to: "/deep-match",
      group: "navigation",
      icon: navIcon(
        <>
          <circle cx="12" cy="12" r="10" />
          <path d="M12 6v6l4 2" />
          <path d="M16 16l2 2" />
        </>,
      ),
    },
    {
      id: "story-bank",
      label: m.nav_story_bank(),
      to: "/story-bank",
      group: "navigation",
      icon: navIcon(
        <>
          <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
        </>,
      ),
    },
    {
      id: "keyword-optimizer",
      label: m.nav_keyword_optimizer(),
      to: "/keyword-optimizer",
      group: "navigation",
      icon: navIcon(
        <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />,
      ),
    },
    {
      id: "portal-scanner",
      label: m.nav_portal_scanner(),
      to: "/portal-scanner",
      group: "navigation",
      icon: navIcon(
        <>
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
        </>,
      ),
    },
    {
      id: "negotiation",
      label: m.nav_negotiation(),
      to: "/negotiation",
      group: "navigation",
      icon: navIcon(
        <>
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 00-3-3.87" />
          <path d="M16 3.13a4 4 0 010 7.75" />
        </>,
      ),
    },

    /* Navigation — Support */
    {
      id: "settings",
      label: m.nav_settings(),
      to: "/settings",
      group: "navigation",
      icon: navIcon(
        <>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
        </>,
      ),
    },
    {
      id: "help",
      label: m.nav_help_center(),
      to: "/help",
      group: "navigation",
      icon: navIcon(
        <>
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </>,
      ),
    },
    {
      id: "feedback",
      label: m.nav_feedback(),
      to: "/feedback",
      group: "navigation",
      icon: navIcon(
        <>
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="8" y1="12" x2="16" y2="12" />
        </>,
      ),
    },

    /* Actions */
    {
      id: "new-resume",
      label: "New Resume",
      to: "/resumes/new",
      group: "actions",
      icon: navIcon(
        <>
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </>,
      ),
    },
    {
      id: "add-job",
      label: "Add Job",
      to: "/jobs/new",
      group: "actions",
      icon: navIcon(
        <>
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </>,
      ),
    },
  ];
}

/* ── Framer-motion variants ── */

const prefersReduced =
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

const overlayVariants = prefersReduced
  ? { hidden: { opacity: 1 }, visible: { opacity: 1 } }
  : { hidden: { opacity: 0 }, visible: { opacity: 1 } };

const panelVariants = prefersReduced
  ? { hidden: { opacity: 1 }, visible: { opacity: 1 } }
  : { hidden: { opacity: 0, scale: 0.96, y: -8 }, visible: { opacity: 1, scale: 1, y: 0 } };

const itemVariants = prefersReduced
  ? { hidden: { opacity: 1 }, visible: () => ({ opacity: 1 }) }
  : {
      hidden: { opacity: 0, y: 4 },
      visible: (i: number) => ({
        opacity: 1,
        y: 0,
        transition: { delay: i * 0.04, duration: 0.15 },
      }),
    };

/* ── Component ── */

export function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  const [query, setQuery] = React.useState("");
  const [activeIndex, setActiveIndex] = React.useState(0);

  const allItems = React.useMemo(() => getCommandItems(), []);

  const filtered = React.useMemo(() => {
    if (!query.trim()) return allItems;
    const q = query.toLowerCase();
    return allItems.filter((item) => item.label.toLowerCase().includes(q));
  }, [query, allItems]);

  const navItems = filtered.filter((i) => i.group === "navigation");
  const actionItems = filtered.filter((i) => i.group === "actions");
  const flatItems = [...navItems, ...actionItems];

  // Reset state when opening
  React.useEffect(() => {
    if (isOpen) {
      setQuery("");
      setActiveIndex(0);
      // Focus input after animation frame
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [isOpen]);

  // Keep activeIndex in bounds
  React.useEffect(() => {
    if (activeIndex >= flatItems.length) {
      setActiveIndex(Math.max(0, flatItems.length - 1));
    }
  }, [flatItems.length, activeIndex]);

  // Scroll active item into view
  React.useEffect(() => {
    if (!listRef.current) return;
    const activeEl = listRef.current.querySelector<HTMLElement>(
      `[data-cmd-index="${activeIndex}"]`,
    );
    activeEl?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  function activate(item: CommandItem) {
    onClose();
    navigate({ to: item.to });
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((prev) =>
          prev < flatItems.length - 1 ? prev + 1 : 0,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((prev) =>
          prev > 0 ? prev - 1 : flatItems.length - 1,
        );
        break;
      case "Enter":
        e.preventDefault();
        if (flatItems[activeIndex]) {
          activate(flatItems[activeIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        onClose();
        break;
    }
  }

  // Build grouped index counter
  let globalIndex = 0;

  function renderItem(item: CommandItem) {
    const idx = globalIndex++;
    return (
      <motion.button
        key={item.id}
        className={`cmd-palette-item ${idx === activeIndex ? "active" : ""}`}
        data-cmd-index={idx}
        custom={idx}
        variants={itemVariants}
        initial="hidden"
        animate="visible"
        onMouseEnter={() => setActiveIndex(idx)}
        onClick={() => activate(item)}
        type="button"
      >
        {item.icon}
        <span>{item.label}</span>
      </motion.button>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="cmd-palette-overlay"
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          transition={{ duration: 0.15 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Command palette"
        >
          <motion.div
            className="cmd-palette"
            variants={panelVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={{ duration: 0.15, ease: [0.22, 1, 0.36, 1] }}
            onKeyDown={handleKeyDown}
          >
            <div className="cmd-palette-input-wrapper">
              {SearchIcon}
              <input
                ref={inputRef}
                className="cmd-palette-input"
                type="text"
                placeholder="Search or jump to..."
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActiveIndex(0);
                }}
                aria-label="Search commands"
                autoComplete="off"
                spellCheck={false}
              />
              <kbd className="cmd-palette-kbd">Esc</kbd>
            </div>

            <div className="cmd-palette-results" ref={listRef}>
              {flatItems.length === 0 && (
                <div className="cmd-palette-empty">No results found</div>
              )}

              {navItems.length > 0 && (
                <div className="cmd-palette-group">
                  <div className="cmd-palette-group-label">Navigation</div>
                  {navItems.map((item) => renderItem(item))}
                </div>
              )}

              {actionItems.length > 0 && (
                <div className="cmd-palette-group">
                  <div className="cmd-palette-group-label">Actions</div>
                  {/* Reset globalIndex for actions group rendering */}
                  {actionItems.map((item) => renderItem(item))}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
